import type { Router } from 'expo-router';
import { InteractionManager, Platform } from 'react-native';
import type { PostAuthTarget } from './auth-handoff';
import { clearDrawerReturn } from './drawer-return';
import {
  saveUaePassPostAuthTarget,
  type UaePassMode,
} from './uae-pass-session';

type OpenUaePassFlowOptions = {
  mode?: UaePassMode;
  dismissCurrentSurface?: boolean;
  postAuthTarget?: PostAuthTarget;
  source?: 'default' | 'auth_login_sheet';
};

let isLaunchingUaePass = false;

function buildUaePassHref(mode: UaePassMode) {
  return mode === 'settings_link'
    ? ({
        pathname: '/uae-pass',
        params: { mode: 'settings_link' },
      } as const)
    : ('/uae-pass' as const);
}

function waitForDismissTransition() {
  return new Promise<void>((resolve) => {
    InteractionManager.runAfterInteractions(() => {
      resolve();
    });
  });
}

export async function openUaePassFlow(
  router: Router,
  options: OpenUaePassFlowOptions = {},
) {
  if (isLaunchingUaePass) {
    return;
  }

  isLaunchingUaePass = true;

  try {
    const mode = options.mode ?? 'sign_in';
    if (mode === 'sign_in' && options.postAuthTarget && options.postAuthTarget !== 'root') {
      saveUaePassPostAuthTarget(options.postAuthTarget);
    }

    const shouldLaunchFromAuthSheet =
      mode === 'sign_in' &&
      Platform.OS === 'ios' &&
      options.source === 'auth_login_sheet';
    const shouldDismissCurrentSurface =
      mode === 'settings_link' &&
      Platform.OS === 'ios' &&
      options.dismissCurrentSurface &&
      router.canDismiss();

    if (shouldLaunchFromAuthSheet) {
      router.push(buildUaePassHref(mode));
      return;
    }

    if (shouldDismissCurrentSurface) {
      // Dismissing Settings on the way somewhere else, not backing out of it:
      // drop the drawer-return mark or the drawer slides open behind the UAE
      // PASS modal and is waiting there when the user comes back.
      clearDrawerReturn();
      router.dismiss();
      await waitForDismissTransition();
    }

    router.push(buildUaePassHref(mode));
  } finally {
    isLaunchingUaePass = false;
  }
}
